import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import RichMessage from './RichMessage';

/** Teal used by every confirmation dialog — kept here so a caller outside
 * CreditManagement (Dashboard's Stripe-redirect flow) renders in the same
 * color without importing CreditManagement's internals. */
export const SUCCESS_DIALOG_TEAL = '#1C7C73';

/**
 * The app's one "it worked" modal, extracted from CreditManagement so it can
 * be mounted from outside it too.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * Buying a credit pack on a saved card never leaves the page and used to show
 * this dialog. Buying one with no card on file redirects to Stripe Checkout
 * and used to come back through a completely different component — a top
 * bar Snackbar in Dashboard — for the same event. Same purchase, two
 * different-looking confirmations. This component is the single surface both
 * paths now render, so they cannot drift again.
 *
 * `parts` (paragraphs of `{ text, strong }`, via RichMessage) is preferred —
 * it gets real spacing between lines. `message` (a plain string, rendered
 * `pre-line`) is kept for producers that only have a string.
 */
export default function SuccessDialog({
  open,
  onClose,
  title,
  tone = 'celebrate',
  message = '',
  parts = null,
}) {
  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
      <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700, color: SUCCESS_DIALOG_TEAL }}>
        {title || (tone === 'neutral' ? 'Done' : 'Success')}
      </DialogTitle>
      <DialogContent>
        {/* pre-line so a message can be several short lines, each answering
            one question, instead of one paragraph the reader has to unpick. */}
        {parts ? (
          <RichMessage
            paragraphs={parts}
            // DialogContentText's colour, so a structured message and a plain
            // one look identical apart from the spacing and the emphasis.
            color="text.secondary"
          />
        ) : (
          /* pre-line so a legacy string message can still be several short
             lines. Producers that supply parts get real paragraph spacing. */
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {message}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} variant="contained" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: SUCCESS_DIALOG_TEAL, '&:hover': { bgcolor: '#156874' } }}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
